import { examCategoryOf } from '@/constants/examCategories';
import { gradeFor } from '@/constants/grading';
import type { BadgeTone } from '@/components/ui/Badge';
import type {
  StudentExamMark,
  StudentProfile,
  StudentProfileRecord,
} from '@/types/student';

/**
 * The profile screen's view model, and the one place the API's shape is turned
 * into it.
 *
 * The screen asks two endpoints — `/student/profile` for the record and
 * `/student/marks` for the papers — and four tab components draw from the
 * result. Mapping once here rather than in each of them is what keeps the
 * personal tab and the academic tab from formatting the same date, or grading
 * the same mark, two different ways (CLAUDE.md §3).
 *
 * Every field below comes from one of those two responses. Where the record
 * has nothing — a child with no mobile of their own, a section whose subjects
 * have no teacher allotted yet — the value is null and the screen says so,
 * rather than a placeholder that reads like a fact.
 */

/** A parent or guardian, as the profile screen prints them. */
export interface ParentInfo {
  name: string;
  relation: string;
  /** Null where the office holds no number for them. */
  phone: string | null;
  email: string | null;
  occupation: string | null;
  /** The household's, not their own — a guardian row holds no address. */
  address: string;
}

/** A contact who is neither parent — the `GUARDIAN` row on the record. */
export interface EmergencyContact {
  id: string;
  name: string;
  /** "Uncle", "Grandmother" — what the office wrote against them. */
  relation: string;
  phone: string | null;
  email: string | null;
  address: string;
}

export interface ProfileSubject {
  id: string;
  /** Position in the curriculum, "01" — the institute's own order. */
  code: string;
  name: string;
  /** Null until the office allots somebody to it for this section. */
  teacher: string | null;
}

/** One paper, totalled across every subject it was sat in. */
export interface ProfileExamProgress {
  exam: string;
  marksObtained: number | null;
  totalMarks: number;
  percentage: number | null;
  grade: string | null;
  /** The band's tone, so the grade is drawn in its own colour. Null with `grade`. */
  gradeTone: BadgeTone | null;
}

/** What a teacher wrote on the child's most recently marked paper. */
export interface ProfileRemark {
  quote: string;
  /** Whoever entered that mark. Null when nobody is recorded — the remark then goes unsigned. */
  author: string | null;
  /** `YYYY-MM-DD` — the day of the paper it was written on. */
  date: string;
}

export interface StudentProfileDetails {
  fullName: string;
  /** "Class V - A" */
  className: string;
  section: string;
  rollNumber: string;
  /** `YYYY-MM-DD`, or null on a record admitted without one. */
  dateOfBirth: string | null;
  /** "9 years", worked out from the date of birth. */
  age: string | null;
  gender: string | null;
  bloodGroup: string | null;
  admissionNumber: string;
  academicYear: string;
  /** The whole household address on one line, or null if none was given. */
  address: string | null;
  studentPhone: string | null;
  studentEmail: string | null;
  status: string;
  house: string | null;
  classTeacher: string | null;
  classTeacherDetails: {
    name: string;
    designation: string | null;
    email: string | null;
    phone: string | null;
    employeeCode: string | null;
  } | null;
  father: ParentInfo | null;
  mother: ParentInfo | null;
  emergencyContacts: EmergencyContact[];
  subjects: ProfileSubject[];
  termExams: ProfileExamProgress[];
  unitTests: ProfileExamProgress[];
  assignments: ProfileExamProgress[];
  /** Null until at least one paper has been marked. */
  overallPerformance: {
    averageScore: number;
    rating: string;
  } | null;
  attendance: {
    totalWorkingDays: number;
    daysPresent: number;
    daysAbsent: number;
    /** Null until a register exists — not a zero, which would read as absent. */
    percentage: number | null;
  };
  remarks: ProfileRemark | null;
}

/** Completed years, the way an admission form counts them. */
function ageInYears(dateOfBirth: string): number {
  const born = new Date(`${dateOfBirth}T00:00:00.000Z`);
  const today = new Date();
  let years = today.getUTCFullYear() - born.getUTCFullYear();

  const monthDiff = today.getUTCMonth() - born.getUTCMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getUTCDate() < born.getUTCDate())) {
    years -= 1;
  }

  return Math.max(years, 0);
}

/** "45 Jubilee Hills Road No 3, Hyderabad, Telangana - 500033" */
function oneLineAddress(address: StudentProfileRecord['address']): string | null {
  // A city district is written once. In the metros the two columns hold the
  // same word, and "Hyderabad, Hyderabad" reads as a mistake on the record.
  const district =
    address.district && address.district.trim().toLowerCase() === address.city?.trim().toLowerCase()
      ? null
      : address.district;

  const street = [address.line1, address.line2, address.city, district, address.state]
    .filter((part): part is string => Boolean(part && part.trim()))
    .join(', ');

  if (!street) return address.pincode ? `PIN ${address.pincode}` : null;

  return address.pincode ? `${street} - ${address.pincode}` : street;
}

/** "MALE" as the record stores it, "Male" as a screen reads it. */
function titleCase(value: string | null): string | null {
  if (!value) return null;
  return value.charAt(0) + value.slice(1).toLowerCase();
}

function toParentInfo(
  guardian: StudentProfileRecord['guardians'][number] | undefined,
  relation: string,
  address: string | null,
): ParentInfo | null {
  if (!guardian) return null;

  return {
    name: guardian.name,
    relation,
    phone: guardian.mobile,
    email: guardian.email,
    occupation: guardian.occupation,
    address: address ?? '—',
  };
}

/**
 * Every paper, totalled across the subjects it was sat in and split into the
 * three groups the academic tab shows.
 *
 * One exam is several rows — one per subject — so "Term 1: 515 out of 600" is
 * the sum of them. Only papers with a score count, on both sides: an absent or
 * not-yet-marked paper adding its maximum against a zero would pull the
 * percentage and grade down for a paper nobody scored, and the Results screen
 * already leaves those papers out — the two must agree on the same exam.
 */
function groupExams(marks: StudentExamMark[]) {
  const groups = {
    termExams: new Map<string, { obtained: number; max: number; scored: boolean }>(),
    unitTests: new Map<string, { obtained: number; max: number; scored: boolean }>(),
    assignments: new Map<string, { obtained: number; max: number; scored: boolean }>(),
  };

  for (const mark of marks) {
    const group = examCategoryOf(mark.examType);
    if (!group) continue;

    const running = groups[group].get(mark.name) ?? { obtained: 0, max: 0, scored: false };

    if (!mark.isAbsent && mark.marksObtained !== null) {
      running.obtained += mark.marksObtained;
      running.max += mark.maxMarks;
      running.scored = true;
    }

    groups[group].set(mark.name, running);
  }

  const toRows = (bucket: Map<string, { obtained: number; max: number; scored: boolean }>) =>
    [...bucket.entries()].map(([exam, totals]): ProfileExamProgress => {
      const band = totals.scored ? gradeFor(totals.obtained, totals.max) : null;

      return {
        exam,
        marksObtained: totals.scored ? totals.obtained : null,
        totalMarks: totals.max,
        percentage:
          totals.scored && totals.max > 0
            ? Math.round((totals.obtained / totals.max) * 100)
            : null,
        grade: band?.grade ?? null,
        gradeTone: band?.tone ?? null,
      };
    });

  return {
    termExams: toRows(groups.termExams),
    unitTests: toRows(groups.unitTests),
    assignments: toRows(groups.assignments),
  };
}

/** Their mean across every paper actually marked this session. */
function overallFrom(marks: StudentExamMark[]) {
  const scored = marks.filter((mark) => !mark.isAbsent && mark.marksObtained !== null);
  if (scored.length === 0) return null;

  const obtained = scored.reduce((total, mark) => total + (mark.marksObtained ?? 0), 0);
  const max = scored.reduce((total, mark) => total + mark.maxMarks, 0);
  if (max <= 0) return null;

  const averageScore = Math.round((obtained / max) * 100);
  const band = gradeFor(obtained, max);

  // The band's name, not its compliment: that line is written in a teacher's
  // voice, and no teacher wrote it about this child.
  return { averageScore, rating: band?.label ?? '' };
}

/**
 * The last thing a teacher wrote on one of their papers.
 *
 * Read from the marks rather than invented: the exam endpoint already carries
 * what the teacher wrote for this child to read, and the papers come back most
 * recent first. Signed by whoever entered the mark — usually the subject
 * teacher, so the class teacher's name here would put words in their mouth.
 */
function latestRemark(marks: StudentExamMark[]): ProfileRemark | null {
  const written = marks.find((mark) => mark.remarks && mark.remarks.trim());
  if (!written?.remarks) return null;

  return {
    quote: written.remarks,
    author: written.gradedBy,
    date: written.examDate,
  };
}

/**
 * The part of `StudentProfile` the profile screen reads. The parent panel has no
 * `/student/me` for the child and sends just this, so it is all that is asked for.
 */
export type ProfileHeader = Pick<StudentProfile, 'section' | 'academicYear' | 'classTeacher'> & {
  classTeacherDetails?: {
    name: string;
    designation: string | null;
    email: string | null;
    phone: string | null;
    employeeCode: string | null;
  } | null;
};

/**
 * The profile screen's whole view model.
 *
 * `profile` is what the panel already read for its sidebar, so the name, class
 * and session come from there rather than being asked for twice.
 */
export function toProfileDetails(
  profile: ProfileHeader | null,
  record: StudentProfileRecord,
  marks: StudentExamMark[],
): StudentProfileDetails {
  const address = oneLineAddress(record.address);
  const father = record.guardians.find((guardian) => guardian.kind === 'FATHER');
  const mother = record.guardians.find((guardian) => guardian.kind === 'MOTHER');
  const { termExams, unitTests, assignments } = groupExams(marks);

  return {
    fullName: record.personal.fullName,
    className: profile?.section?.displayName ?? 'Not placed yet',
    section: profile?.section?.sectionName ?? '—',
    rollNumber: record.admission.rollNumber ? `#${record.admission.rollNumber}` : '—',
    dateOfBirth: record.personal.dateOfBirth,
    age: record.personal.dateOfBirth
      ? `${ageInYears(record.personal.dateOfBirth)} years`
      : null,
    gender: titleCase(record.personal.gender),
    bloodGroup: record.personal.bloodGroup === 'UNKNOWN' ? null : record.personal.bloodGroup,
    admissionNumber: record.admission.admissionNumber,
    academicYear: profile?.academicYear?.name ?? '—',
    address,
    studentPhone: record.personal.mobile,
    studentEmail: record.personal.email,
    status: titleCase(record.personal.status) ?? 'Active',
    house: record.personal.house ?? null,
    classTeacher: profile?.classTeacherDetails?.name ?? profile?.classTeacher ?? null,
    classTeacherDetails: profile?.classTeacherDetails ?? null,
    father: toParentInfo(father, 'Father', address),
    mother: toParentInfo(mother, 'Mother', address),
    emergencyContacts: record.guardians
      .filter((guardian) => guardian.kind === 'GUARDIAN')
      .map((guardian) => ({
        id: guardian.uuid,
        name: guardian.name,
        relation: guardian.relationshipLabel ?? 'Guardian',
        phone: guardian.mobile,
        email: guardian.email,
        address: address ?? '—',
      })),
    subjects: record.subjects.map((subject, index) => ({
      id: subject.uuid,
      code: String(index + 1).padStart(2, '0'),
      name: subject.name,
      teacher: subject.teacherName,
    })),
    termExams,
    unitTests,
    assignments,
    overallPerformance: overallFrom(marks),
    attendance: {
      totalWorkingDays: record.attendance.markedDays,
      daysPresent: record.attendance.attendedDays,
      // Marked absent only. Marked days less attended ones would count approved
      // leave as absence too.
      daysAbsent: record.attendance.absentDays,
      percentage: record.attendance.percentage,
    },
    remarks: latestRemark(marks),
  };
}
